/**
 * Transaction Summary (Ringkasan Transaksi) — on-screen aggregated report.
 *
 * For a GMT+7 date range it shows headline totals (Total Cash In / Cash Out /
 * Fee — PAID transactions only) plus two breakdowns: CASH-IN grouped by payment
 * method and CASH-OUT grouped by destination bank. Unlike the file-export
 * "Transaction Reports" page, nothing is downloaded — this is a live view.
 *
 * Backend `GET /api/reports/summary` applies the scope rule (external → own
 * merchant; internal may filter by merchantId, or with report.transaction.view.all
 * see platform-wide).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Center,
  Chip,
  Grid,
  Group,
  Loader,
  MultiSelect,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconCoin,
  IconSearch,
} from '@tabler/icons-react';
import { summaryApi } from '../api/summary';
import { merchantsApi } from '../api/merchants';
import { ApiCallError, describeError } from '../api/client';
import { useSession, usePermissions } from '../auth/SessionContext';
import { isPlatform } from '../auth/permissions';
import { formatIdr } from '../lib/format';
import { useTranslation } from '../i18n/LocaleContext';
import type { TranslationKey } from '../i18n/translations';

function toIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Quick preset ranges shown as chips above the date picker. Selecting one just
// populates the range (Apply still commits it), matching the filter pattern.
type PresetKey =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear';

const PRESETS: { key: PresetKey; labelKey: TranslationKey }[] = [
  { key: 'today', labelKey: 'summary.presetToday' },
  { key: 'yesterday', labelKey: 'summary.presetYesterday' },
  { key: 'last7', labelKey: 'summary.presetLast7' },
  { key: 'last30', labelKey: 'summary.presetLast30' },
  { key: 'thisMonth', labelKey: 'summary.presetThisMonth' },
  { key: 'lastMonth', labelKey: 'summary.presetLastMonth' },
  { key: 'thisYear', labelKey: 'summary.presetThisYear' },
  { key: 'lastYear', labelKey: 'summary.presetLastYear' },
];

const DEFAULT_PRESET: PresetKey = 'last7';

// All computed in local time so they line up with toIsoDate() (which reads the
// local Y/M/D). The backend re-interprets the YYYY-MM-DD in GMT+7.
function presetRange(key: PresetKey): [Date, Date] {
  const now = new Date();
  const atMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = atMidnight(now);
  const shift = (base: Date, days: number) => {
    const x = new Date(base);
    x.setDate(x.getDate() + days);
    return x;
  };
  switch (key) {
    case 'today':
      return [today, today];
    case 'yesterday': {
      const y = shift(today, -1);
      return [y, y];
    }
    case 'last7':
      return [shift(today, -6), today];
    case 'last30':
      return [shift(today, -29), today];
    case 'thisMonth':
      return [new Date(now.getFullYear(), now.getMonth(), 1), today];
    case 'lastMonth':
      return [
        new Date(now.getFullYear(), now.getMonth() - 1, 1),
        new Date(now.getFullYear(), now.getMonth(), 0), // day 0 = last day of prev month
      ];
    case 'thisYear':
      return [new Date(now.getFullYear(), 0, 1), today];
    case 'lastYear':
      return [
        new Date(now.getFullYear() - 1, 0, 1),
        new Date(now.getFullYear() - 1, 11, 31),
      ];
  }
}

type KpiProps = {
  label: string;
  value: string;
  hint?: string;
  icon: typeof IconCoin;
  color?: string;
};

function Kpi({ label, value, hint, icon: Icon, color = 'brand' }: KpiProps) {
  return (
    <Card h="100%">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Text c="dimmed" size="xs" tt="uppercase" fw={600}>
            {label}
          </Text>
          <Text size="xl" fw={700}>
            {value}
          </Text>
          {hint && (
            <Text c="dimmed" size="xs">
              {hint}
            </Text>
          )}
        </Stack>
        <ThemeIcon color={color} variant="light" size={40} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

type DetailRow = { product: string; trxCount: number; grossIncome: number; fee: number };

function DetailTable(props: {
  title: string;
  productHeader: string;
  colTrx: string;
  colGross: string;
  colFee: string;
  empty: string;
  searchPlaceholder: string;
  rows: DetailRow[];
  search: string;
  onSearch: (v: string) => void;
}) {
  return (
    <Card withBorder p={0}>
      <Group justify="space-between" p="md" pb="sm" wrap="nowrap">
        <Text fw={600}>{props.title}</Text>
        <TextInput
          size="xs"
          leftSection={<IconSearch size={14} />}
          placeholder={props.searchPlaceholder}
          value={props.search}
          onChange={(e) => props.onSearch(e.currentTarget.value)}
        />
      </Group>
      <ScrollArea>
        <Table striped highlightOnHover miw={520}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={56}>No.</Table.Th>
              <Table.Th>{props.productHeader}</Table.Th>
              <Table.Th ta="right">{props.colTrx}</Table.Th>
              <Table.Th ta="right">{props.colGross}</Table.Th>
              <Table.Th ta="right">{props.colFee}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {props.rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center" py="md">
                    {props.empty}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              props.rows.map((r, i) => (
                <Table.Tr key={r.product}>
                  <Table.Td>{i + 1}</Table.Td>
                  <Table.Td>{r.product}</Table.Td>
                  <Table.Td ta="right">{r.trxCount.toLocaleString('id-ID')}</Table.Td>
                  <Table.Td ta="right">{formatIdr(r.grossIncome)}</Table.Td>
                  <Table.Td ta="right">{formatIdr(r.fee)}</Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Card>
  );
}

export function TransactionSummaryPage() {
  const { t } = useTranslation();
  const { state } = useSession();
  const { has, isSuperuser } = usePermissions();
  const isPlatformUser = state.status === 'authenticated' && isPlatform(state.profile);
  const canViewAll = isSuperuser || has('report.transaction.view.all');

  const [draft, setDraft] = useState<{ range: [Date | null, Date | null]; merchantIds: string[] }>({
    range: presetRange(DEFAULT_PRESET),
    merchantIds: [],
  });
  const [applied, setApplied] = useState<typeof draft>(draft);
  const [preset, setPreset] = useState<PresetKey | null>(DEFAULT_PRESET);
  const [searchIn, setSearchIn] = useState('');
  const [searchOut, setSearchOut] = useState('');

  // Selecting a preset chip populates the range (Apply commits it).
  const choosePreset = (key: PresetKey) => {
    setPreset(key);
    setDraft((d) => ({ ...d, range: presetRange(key) }));
  };

  // Merchants list for the (internal-only) filter dropdown. The list endpoint
  // caps `limit` at 100 (higher values 400), so request exactly the max.
  const merchantsQuery = useQuery({
    queryKey: ['merchants-for-summary-filter'],
    queryFn: () => merchantsApi.list({ limit: 100 }),
    enabled: isPlatformUser && canViewAll,
  });

  const summaryQuery = useQuery({
    queryKey: ['reports.summary', applied],
    queryFn: () =>
      summaryApi.get({
        from: toIsoDate(applied.range[0]),
        to: toIsoDate(applied.range[1]),
        merchantIds: applied.merchantIds.length > 0 ? applied.merchantIds : undefined,
      }),
  });

  const applyFilter = () => setApplied(draft);
  const resetFilter = () => {
    const e = {
      range: presetRange(DEFAULT_PRESET) as [Date | null, Date | null],
      merchantIds: [] as string[],
    };
    setPreset(DEFAULT_PRESET);
    setDraft(e);
    setApplied(e);
  };

  const data = summaryQuery.data;
  const cashIn = useMemo(() => {
    const rows = (data?.cashIn ?? []).map((r) => ({
      product: r.method,
      trxCount: r.trxCount,
      grossIncome: r.grossIncome,
      fee: r.fee,
    }));
    const s = searchIn.trim().toLowerCase();
    return s ? rows.filter((r) => r.product.toLowerCase().includes(s)) : rows;
  }, [data, searchIn]);
  const cashOut = useMemo(() => {
    const rows = (data?.cashOut ?? []).map((r) => ({
      product: r.product,
      trxCount: r.trxCount,
      grossIncome: r.grossIncome,
      fee: r.fee,
    }));
    const s = searchOut.trim().toLowerCase();
    return s ? rows.filter((r) => r.product.toLowerCase().includes(s)) : rows;
  }, [data, searchOut]);

  return (
    <Stack gap="md">
      <div>
        <Title order={2}>{t('summary.title')}</Title>
        <Text c="dimmed" size="sm">
          {data ? `${data.range.from} – ${data.range.to}` : t('summary.subtitle')}
        </Text>
      </div>

      <Card withBorder>
        <Stack gap="sm">
          <Chip.Group
            multiple={false}
            value={preset ?? ''}
            onChange={(v) => {
              if (v) choosePreset(v as PresetKey);
            }}
          >
            <Group gap="xs">
              {PRESETS.map((p) => (
                <Chip key={p.key} value={p.key} size="xs" radius="sm">
                  {t(p.labelKey)}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
          <Group grow align="end">
            <DatePickerInput
              type="range"
              allowSingleDateInRange
              label={t('summary.dateRange')}
              value={draft.range}
              onChange={(v) => {
                setPreset(null); // manual edit → custom range
                setDraft({ ...draft, range: v as [Date | null, Date | null] });
              }}
              clearable
            />
            {isPlatformUser && canViewAll && (
              <MultiSelect
                label={t('reports.merchantFilter')}
                placeholder={draft.merchantIds.length === 0 ? t('reports.merchantAll') : undefined}
                clearable
                searchable
                hidePickedOptions
                nothingFoundMessage={t('users.merchantNothingFound')}
                data={(merchantsQuery.data?.rows ?? []).map((m) => ({
                  value: m.id,
                  label: m.name,
                }))}
                value={draft.merchantIds}
                onChange={(v) => setDraft({ ...draft, merchantIds: v })}
              />
            )}
          </Group>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={resetFilter}>
              {t('common.reset')}
            </Button>
            <Button size="xs" onClick={applyFilter}>
              {t('common.apply')}
            </Button>
          </Group>
        </Stack>
      </Card>

      {summaryQuery.isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : summaryQuery.error ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {summaryQuery.error instanceof ApiCallError
            ? `${summaryQuery.error.code}: ${summaryQuery.error.message}`
            : describeError(summaryQuery.error)}
        </Alert>
      ) : (
        <>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Kpi
                label={t('summary.kpiCashIn')}
                value={formatIdr(data?.totals.cashIn.amount ?? 0)}
                hint={t('summary.kpiTrxPaid', {
                  count: (data?.totals.cashIn.count ?? 0).toLocaleString('id-ID'),
                })}
                icon={IconArrowDown}
                color="teal"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Kpi
                label={t('summary.kpiCashOut')}
                value={formatIdr(data?.totals.cashOut.amount ?? 0)}
                hint={t('summary.kpiTrxPaid', {
                  count: (data?.totals.cashOut.count ?? 0).toLocaleString('id-ID'),
                })}
                icon={IconArrowUp}
                color="orange"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Kpi
                label={t('summary.kpiFee')}
                value={formatIdr(data?.totals.fee ?? 0)}
                hint={t('summary.kpiFeeHint')}
                icon={IconCoin}
                color="grape"
              />
            </Grid.Col>
          </Grid>

          <DetailTable
            title={t('summary.cashInDetail')}
            productHeader={t('summary.colProduct')}
            colTrx={t('summary.colTrxCount')}
            colGross={t('summary.colGross')}
            colFee={t('summary.colFee')}
            empty={t('summary.empty')}
            searchPlaceholder={t('summary.searchPlaceholder')}
            rows={cashIn}
            search={searchIn}
            onSearch={setSearchIn}
          />

          <DetailTable
            title={t('summary.cashOutDetail')}
            productHeader={t('summary.colProduct')}
            colTrx={t('summary.colTrxCount')}
            colGross={t('summary.colGross')}
            colFee={t('summary.colFee')}
            empty={t('summary.empty')}
            searchPlaceholder={t('summary.searchPlaceholder')}
            rows={cashOut}
            search={searchOut}
            onSearch={setSearchOut}
          />
        </>
      )}
    </Stack>
  );
}
